import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';

import { previewSourceDeduction } from '@/api/sources';
import {
  describeDeletion, localProblems, newPeriodRule, newPerOrderRule, newRuleSet,
  toEditor, toPayload, withWindows,
} from '@/utils/deductionRules';

// Matches the server's own limit, so the box stops before the refusal does.
const LABEL_LIMIT = 60;
const money = value => (typeof value === 'number' ? `$${value.toFixed(2)}` : '—');
const today = () => new Date().toISOString().slice(0, 10);

/**
 * What a Source charges, and what an order is left worth after it.
 *
 * The preview is the point of this screen. A mistyped rate produces a
 * believable figure rather than a visible error, so the owner needs to see a
 * sample order priced charge by charge and check it against a real statement
 * before saving. It is priced by the server for the same reason the figures
 * on the dashboard are: one implementation, so the two cannot disagree.
 */
export default function DeductionRulesBreakout({ source, blocked, onSave, onClose }) {
  const [sets, setSets] = useState(() => toEditor(source?.deduction_rule_sets));
  const [selectedKey, setSelectedKey] = useState(null);
  const [sample, setSample] = useState('100.00');
  const [preview, setPreview] = useState({ status: 'idle' });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  const ordered = useMemo(() => withWindows(sets), [sets]);
  const selected = sets.find(set => set.key === selectedKey) || null;
  const problems = useMemo(() => localProblems(sets), [sets]);

  const editSet = useCallback((key, change) => {
    setSets(current => current.map(set => (set.key === key ? { ...set, ...change(set) } : set)));
  }, []);

  // Priced after a pause rather than on every keystroke: the owner is reading
  // the result, not watching it flicker.
  const previewKey = selected ? JSON.stringify([toPayload([selected])[0].rules, sample]) : null;
  useEffect(() => {
    if (!selected) { setPreview({ status: 'idle' }); return undefined; }
    const rules = toPayload([selected])[0].rules;
    if (rules.length === 0) { setPreview({ status: 'empty' }); return undefined; }
    setPreview(current => ({ ...current, status: 'loading' }));
    const timer = setTimeout(async () => {
      try {
        const data = await previewSourceDeduction({ rules, amount: String(sample).trim() });
        if (live.current) setPreview({ status: 'ready', data });
      } catch (error) {
        // Never a stale or locally guessed figure: a number that looks
        // computed but is not is worse than saying nothing.
        if (live.current) {
          setPreview({
            status: 'error',
            detail: error?.response?.status === 400
              ? (error.response.data?.detail || 'These rules cannot be priced yet.')
              : 'The preview is unavailable right now.',
          });
        }
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  const save = async () => {
    if (blocked || problems.length > 0 || saving) return;
    setSaving(true);
    const accepted = await onSave(toPayload(sets));
    if (!live.current) return;
    setSaving(false);
    if (accepted) onClose?.();
  };

  const removeSet = (key) => {
    setSets(current => current.filter(set => set.key !== key));
    if (selectedKey === key) setSelectedKey(null);
    setPendingDelete(null);
  };

  // Laid out as a table rather than a row of controls: like fields sit under
  // like fields, so a column can be read down. The name comes first because
  // that is the order the work happens in — this is the charge I am recording,
  // and then what kind and how much.
  // Every track is an explicit width except the one flexible column. The
  // header and the rows are separate grid containers, so an `auto` track would
  // size to each one's own contents — the header's action cell is empty while
  // a row's holds three buttons — and the flexible column would then resolve
  // differently in each, leaving the headings off their columns.
  const ROW = 'grid grid-cols-[1.25rem_minmax(6rem,1fr)_6.5rem_5rem_9.5rem_5.25rem] gap-2 items-center';
  // The breakout is often narrower than six columns need. Scrolling keeps the
  // columns aligned and legible; letting them shrink collapsed the flexible
  // Name track to nothing and overlapped the headings. The header travels
  // inside the same scroller as the rows, or it would slide out of step.
  const TABLE = 'overflow-x-auto';
  const TABLE_INNER = 'min-w-[33.5rem] space-y-2';

  const renderPerOrder = (rule, index, total) => (
    <div key={rule.key} className={ROW} data-testid={`per-order-rule-${index}`}>
      <span className="text-xs text-stone-400 text-right">{index + 1}</span>
      <Input
        value={rule.label} aria-label={`Name for charge ${index + 1}`} maxLength={LABEL_LIMIT}
        placeholder="What is this for?" className="h-8 w-full min-w-0 text-sm"
        onChange={event => editSet(selected.key, set => ({
          perOrder: set.perOrder.map(row => (row.key === rule.key ? { ...row, label: event.target.value } : row)),
        }))}
      />
      <select
        value={rule.charge} aria-label="Charge type" className="h-8 w-full text-sm border border-stone-200 rounded-md px-1"
        onChange={event => editSet(selected.key, set => ({
          perOrder: set.perOrder.map(row => (row.key === rule.key ? { ...row, charge: event.target.value } : row)),
        }))}
      >
        <option value="flat">$ amount</option>
        <option value="percent">% share</option>
      </select>
      <Input
        value={rule.amount} aria-label={rule.charge === 'percent' ? 'Percent' : 'Amount'}
        aria-invalid={!String(rule.amount).trim() || undefined}
        placeholder={rule.charge === 'percent' ? '10' : '5.00'}
        // An empty box is marked where it is, not only described underneath.
        className={`h-8 w-full text-sm ${!String(rule.amount).trim() ? 'border-red-400 bg-red-50' : ''}`}
        onChange={event => editSet(selected.key, set => ({
          perOrder: set.perOrder.map(row => (row.key === rule.key ? { ...row, amount: event.target.value } : row)),
        }))}
      />
      {/* Only a percentage has something to be a percentage of. The cell is
          held empty for a flat charge so the column below it stays aligned. */}
      {rule.charge === 'percent' ? (
        <select
          value={rule.basis} aria-label="Taken from" className="h-8 w-full text-sm border border-stone-200 rounded-md px-1"
          onChange={event => editSet(selected.key, set => ({
            perOrder: set.perOrder.map(row => (row.key === rule.key ? { ...row, basis: event.target.value } : row)),
          }))}
        >
          <option value="running">of what is left</option>
          <option value="original">of the order total</option>
        </select>
      ) : <span aria-hidden="true" />}
      <div className="flex items-center gap-1">
        <button
          type="button" title="Move up" aria-label={`Move charge ${index + 1} up`} disabled={index === 0}
          className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"
          onClick={() => editSet(selected.key, set => {
            const next = [...set.perOrder];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return { perOrder: next };
          })}
        ><ArrowUp className="h-3.5 w-3.5" /></button>
        <button
          type="button" title="Move down" aria-label={`Move charge ${index + 1} down`} disabled={index === total - 1}
          className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"
          onClick={() => editSet(selected.key, set => {
            const next = [...set.perOrder];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return { perOrder: next };
          })}
        ><ArrowDown className="h-3.5 w-3.5" /></button>
        <button
          type="button" title="Remove" aria-label={`Remove charge ${index + 1}`}
          className="p-1 text-stone-400 hover:text-red-600"
          onClick={() => editSet(selected.key, set => ({ perOrder: set.perOrder.filter(row => row.key !== rule.key) }))}
        ><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );

  // Recurring charges have no type or basis column, so they get their own
  // grid — but the name still leads, so both sections read the same way down.
  const PERIOD_ROW = 'grid grid-cols-[minmax(6rem,1fr)_5rem_10.5rem_1.75rem] gap-2 items-center';
  const PERIOD_TABLE = 'overflow-x-auto';
  const PERIOD_TABLE_INNER = 'min-w-[24rem] space-y-2';
  const COLUMN_HEADS = 'text-[10px] font-semibold uppercase tracking-wider text-stone-400';


  return (
    <Card role="region" aria-label="Charging rules" data-testid="deduction-rules-breakout">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-lg">{source?.name}</CardTitle>
          <p className="text-xs text-stone-500">What this source charges, and when each arrangement applied.</p>
        </div>
        <button onClick={onClose} aria-label="Close charging rules" className="text-stone-400 hover:text-stone-600">
          <X className="h-4 w-4" />
        </button>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-1">
          {ordered.length === 0 && (
            <p className="text-sm text-stone-400" data-testid="no-rule-sets">
              No charges recorded. Orders from this source net their full amount.
            </p>
          )}
          {ordered.map(set => (
            <div key={set.key} className="flex items-center gap-2">
              <button
                type="button" data-testid={`rule-set-${set.key}`}
                onClick={() => setSelectedKey(set.key === selectedKey ? null : set.key)}
                className={`flex-1 text-left text-sm px-2 py-1.5 rounded-md border ${
                  set.key === selectedKey ? 'border-stone-400 bg-stone-50' : 'border-stone-200 hover:bg-stone-50'}`}
              >
                <span className="font-medium text-stone-800">{set.start_date || 'No start date'}</span>
                <span className="text-stone-500">
                  {set.endsOn ? ` to ${set.endsOn}` : ' onward'}
                  {set.isCurrent ? ' · current' : ' · retired'}
                </span>
              </button>
              <button
                type="button" title="Delete this set" aria-label={`Delete rules starting ${set.start_date}`}
                className="p-1.5 text-stone-400 hover:text-red-600"
                onClick={() => setPendingDelete(set.key)}
              ><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <Button
            variant="outline" size="sm" className="mt-1" data-testid="add-rule-set"
            onClick={() => {
              const created = newRuleSet(today());
              setSets(current => [...current, created]);
              setSelectedKey(created.key);
            }}
          ><Plus className="h-3.5 w-3.5 mr-1" /> New rules from a date</Button>
        </div>

        {/* Deleting leaves no gap — the previous set stretches over the window.
            Said plainly, with the real dates, and then obeyed. */}
        {pendingDelete && (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="delete-rule-set-warning">
            <p className="text-xs text-amber-900 flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {describeDeletion(sets, pendingDelete)}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPendingDelete(null)}>Keep it</Button>
              <Button size="sm" data-testid="confirm-delete-rule-set" onClick={() => removeSet(pendingDelete)}>Delete anyway</Button>
            </div>
          </div>
        )}

        {selected && (
          <div className="space-y-4 border-t border-stone-100 pt-4" data-testid="rule-set-editor">
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block" htmlFor="rule-set-start">In force from</label>
              <Input
                id="rule-set-start" type="date" value={selected.start_date} className="h-8 w-44 text-sm"
                onChange={event => editSet(selected.key, () => ({ start_date: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Charged on every order, in this order</p>
              {selected.perOrder.length === 0
                ? <p className="text-xs text-stone-400">Nothing is taken from each order.</p>
                : (
                  <div className={TABLE}>
                    <div className={TABLE_INNER}>
                      <div className={`${ROW} ${COLUMN_HEADS}`} aria-hidden="true">
                        <span /><span>Name</span><span>Type</span><span>Amount</span><span>Taken from</span><span />
                      </div>
                      {selected.perOrder.map((rule, index) => renderPerOrder(rule, index, selected.perOrder.length))}
                    </div>
                  </div>
                )}
              <Button
                variant="outline" size="sm" data-testid="add-per-order-rule"
                onClick={() => editSet(selected.key, set => ({ perOrder: [...set.perOrder, newPerOrderRule()] }))}
              ><Plus className="h-3.5 w-3.5 mr-1" /> Add a charge</Button>
            </div>

            <div className="space-y-2">
              {/* Recurring charges have no order worth setting: each is a flat
                  amount owed on its own cycle, independent of the others. */}
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Recurring charges</p>
              {selected.period.length > 0 && (
                <div className={PERIOD_TABLE}>
                  <div className={PERIOD_TABLE_INNER}>
                    <div className={`${PERIOD_ROW} ${COLUMN_HEADS}`} aria-hidden="true">
                      <span>Name</span><span>Amount</span><span>How often</span><span />
                    </div>
                    {selected.period.map((rule, index) => (
                <div key={rule.key} className={PERIOD_ROW} data-testid={`period-rule-${index}`}>
                  <Input
                    value={rule.label} aria-label={`Name for recurring charge ${index + 1}`} maxLength={LABEL_LIMIT}
                    placeholder="What is this for?" className="h-8 w-full min-w-0 text-sm"
                    onChange={event => editSet(selected.key, set => ({
                      period: set.period.map(row => (row.key === rule.key ? { ...row, label: event.target.value } : row)),
                    }))}
                  />
                  <Input
                    value={rule.amount} aria-label="Recurring amount" placeholder="50.00"
                    aria-invalid={!String(rule.amount).trim() || undefined}
                    className={`h-8 w-full text-sm ${!String(rule.amount).trim() ? 'border-red-400 bg-red-50' : ''}`}
                    onChange={event => editSet(selected.key, set => ({
                      period: set.period.map(row => (row.key === rule.key ? { ...row, amount: event.target.value } : row)),
                    }))}
                  />
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-stone-500">every</span>
                    <Input
                      value={rule.every_days} aria-label="Days between charges" placeholder="30" className="h-8 w-14 text-sm"
                      onChange={event => editSet(selected.key, set => ({
                        period: set.period.map(row => (row.key === rule.key ? { ...row, every_days: event.target.value } : row)),
                      }))}
                    />
                    <span className="text-xs text-stone-500">days</span>
                  </div>
                  <button
                    type="button" title="Remove" aria-label={`Remove recurring charge ${index + 1}`}
                    className="p-1 text-stone-400 hover:text-red-600"
                    onClick={() => editSet(selected.key, set => ({ period: set.period.filter(row => row.key !== rule.key) }))}
                  ><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
                  </div>
                </div>
              )}
              <Button
                variant="outline" size="sm" data-testid="add-period-rule"
                onClick={() => editSet(selected.key, set => ({ period: [...set.period, newPeriodRule()] }))}
              ><Plus className="h-3.5 w-3.5 mr-1" /> Add a recurring charge</Button>
            </div>

            <div className="border border-stone-200 rounded-lg p-3 space-y-2 bg-stone-50" data-testid="deduction-preview">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500" htmlFor="preview-amount">On an order of</label>
                <Input
                  id="preview-amount" value={sample} className="h-8 w-24 text-sm"
                  onChange={event => setSample(event.target.value)}
                />
              </div>
              {preview.status === 'empty' && <p className="text-xs text-stone-400">Add a charge to see what an order nets.</p>}
              {preview.status === 'loading' && <p className="text-xs text-stone-400">Working it out…</p>}
              {/* Labelled and boxed so it reads as this panel's own failure.
                  Unlabelled red text here was indistinguishable from the
                  save-blocking problems further down the card. */}
              {preview.status === 'error' && (
                <div className="flex gap-2 text-xs text-stone-700 bg-white border border-stone-200 rounded-md p-2" role="alert">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
                  <span>
                    <span className="font-semibold">This preview could not be worked out. </span>
                    {preview.detail} Your charges are unaffected — this only stops the sample being priced.
                  </span>
                </div>
              )}
              {preview.status === 'ready' && (
                <div className="space-y-1">
                  {preview.data.steps.map((step, index) => (
                    <div key={index} className="flex justify-between text-xs text-stone-600" data-testid={`preview-step-${index}`}>
                      <span>
                        {step.label ? `${step.label} — ` : ''}
                        {step.charge === 'percent'
                          ? `${step.rate}% ${step.basis === 'original' ? 'of the order total' : 'of what is left'}`
                          : `$${step.rate}`}
                      </span>
                      <span>−{money(step.took)} → {money(step.remaining)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold text-stone-900 border-t border-stone-200 pt-1">
                    <span>You keep</span><span data-testid="preview-net">{money(preview.data.net)}</span>
                  </div>
                  {preview.data.recurring.map((rule, index) => (
                    <p key={index} className="text-xs text-stone-500" data-testid={`preview-recurring-${index}`}>
                      Plus {rule.label ? `${rule.label} — ` : ''}{money(rule.amount)} every {rule.every_days} days,
                      spread across whatever period you are viewing.
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Kept with the Save button it blocks, and labelled, so it is not
            mistaken for something the preview is saying. */}
        {problems.length > 0 && (
          <div className="border border-red-200 bg-red-50 rounded-lg p-3" data-testid="rule-problems">
            <p className="text-xs font-semibold text-red-800">Before these charges can be saved:</p>
            <ul className="text-xs text-red-700 mt-1 space-y-0.5 list-disc list-inside">
              {problems.map(problem => <li key={problem}>{problem}</li>)}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-stone-100 pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save} data-testid="save-rules"
            disabled={blocked || saving || problems.length > 0}
          >{saving ? 'Saving…' : 'Save charges'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
